import { BOND_TYPES_CATALOG } from '@lume/shared';
import { useMemo, useState } from 'react';

import {
  useEstablishLink,
  useLinkCandidates,
} from '../features/conversations/hooks/use-establish-link';

import { CapabilityGate } from './CapabilityGate';

import type { EstablishLinkTargetWire, LinkCandidateWire } from '../lib/types';

export interface EstablishLinkModalProps {
  conversationId: string;
  /** Nome exibido do contato (para o título/contexto). */
  personName: string;
  open: boolean;
  onClose: () => void;
}

type Selection =
  | { mode: 'existing-bond'; bondId: string; label: string; personExternalId?: string }
  | {
      mode: 'create-native';
      bondType: string;
      externalReference?: string;
      label: string;
      personExternalId?: string;
    };

const ROLE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'titular', label: 'Titular' },
  { value: 'autorizado', label: 'Autorizado' },
  { value: 'dependente', label: 'Dependente' },
  { value: 'relacionado', label: 'Relacionado' },
];

function candidateToSelection(c: LinkCandidateWire): Selection {
  // Nativo → anexa ao Bond existente. Externo → cria Bond nativo com o nº externo
  // (Item 391: o registro é sempre nativo; o externo segue fonte-da-verdade viva).
  // ADR-153: o id da PESSOA no sistema externo viaja junto em AMBOS os modos — é ele que
  // as leituras usam. Sem ele o vínculo nasce cego e o ERP responde vazio em silêncio.
  const personRef =
    c.personExternalId !== undefined ? { personExternalId: c.personExternalId } : {};
  if (c.source === 'native' && c.bondId !== undefined) {
    return { mode: 'existing-bond', bondId: c.bondId, label: c.label, ...personRef };
  }
  return {
    mode: 'create-native',
    bondType: c.bondType,
    ...(c.externalReference !== undefined && { externalReference: c.externalReference }),
    label: c.label,
    ...personRef,
  };
}

/**
 * EstablishLinkModal (F2-a) — o operador ESTABELECE o vínculo (Bond↔Person) que
 * falta na conversa: localiza candidatos (nativos + externo best-effort) ou cria
 * um vínculo nativo, escolhe o papel e, para titular, avaliza (confirma a
 * identidade). Fecha o 409 «cliente sem participação declarada» do confirm-link.
 *
 * NEUTRO: nunca "contrato" — o tipo/label vêm do catálogo de Bond. Gated por
 * `customer-link-establish` (opt-in; ativação = TL). Ato auditado no servidor.
 */
export function EstablishLinkModal({
  conversationId,
  personName,
  open,
  onClose,
}: EstablishLinkModalProps): JSX.Element | null {
  const { establish, isPending } = useEstablishLink();
  const [identifierInput, setIdentifierInput] = useState('');
  const [searchIdentifier, setSearchIdentifier] = useState<string | undefined>(undefined);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [creating, setCreating] = useState(false);
  const [createBondType, setCreateBondType] = useState<string>(BOND_TYPES_CATALOG[0]?.id ?? '');
  const [role, setRole] = useState<string>('titular');

  const { data, isLoading } = useLinkCandidates(conversationId, searchIdentifier, open);
  const isTitular = role === 'titular';

  const candidates = data?.candidates ?? [];
  const externalAvailable = data?.externalAvailable ?? false;

  const selectionForCreate = useMemo<Selection | null>(
    () =>
      creating && createBondType.length > 0
        ? {
            mode: 'create-native',
            bondType: createBondType,
            label: 'Novo vínculo',
            ...(identifierInput.length > 0 && { externalReference: identifierInput }),
          }
        : null,
    [creating, createBondType, identifierInput],
  );
  const activeSelection = creating ? selectionForCreate : selection;

  if (!open) return null;

  const submit = (vouch: boolean): void => {
    if (activeSelection === null) return;
    // ADR-153 — a chave de leitura por PESSOA acompanha os dois modos.
    const personRef =
      activeSelection.personExternalId !== undefined
        ? { personExternalId: activeSelection.personExternalId }
        : {};
    const target: EstablishLinkTargetWire =
      activeSelection.mode === 'existing-bond'
        ? { mode: 'existing-bond', bondId: activeSelection.bondId, ...personRef }
        : {
            mode: 'create-native',
            bondType: activeSelection.bondType,
            ...(activeSelection.externalReference !== undefined && {
              externalReference: activeSelection.externalReference,
            }),
            ...personRef,
          };
    void establish({ conversationId, target, role, isTitular, vouch })
      .then(() => onClose())
      .catch(() => {
        /* toast de erro já emitido pelo hook; mantém o modal aberto */
      });
  };

  const canSubmit = activeSelection !== null && !isPending;

  return (
    <CapabilityGate capability="customer-link-establish">
      <div
        role="dialog"
        aria-label="Estabelecer vínculo"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div
          className="flex w-full max-w-lg flex-col rounded-lg shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <div
            className="flex items-start justify-between border-b p-5"
            style={{ borderColor: 'var(--border-default)' }}
          >
            <div>
              <h2 className="text-base font-semibold">Estabelecer vínculo</h2>
              <p className="mt-1 text-xs" style={{ color: 'var(--fg-2)' }}>
                Vincule <strong>{personName}</strong> a um vínculo existente ou crie um novo.
              </p>
            </div>
            <button
              type="button"
              aria-label="Fechar"
              className="text-lg leading-none"
              style={{ color: 'var(--fg-3)' }}
              onClick={onClose}
            >
              ×
            </button>
          </div>

          <div className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-2">
              <span
                className="text-2xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--fg-3)' }}
              >
                Localizar
              </span>
              <div className="flex gap-2">
                <input
                  className="flex-1 rounded border px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)' }}
                  placeholder="Nº do vínculo, telefone…"
                  value={identifierInput}
                  onChange={(e) => setIdentifierInput(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded border px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--border-default)' }}
                  onClick={() => setSearchIdentifier(identifierInput)}
                >
                  Buscar
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span
                className="text-2xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--fg-3)' }}
              >
                Vínculos encontrados
              </span>
              {isLoading ? (
                <p className="text-xs" style={{ color: 'var(--fg-3)' }}>
                  Carregando…
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {candidates.map((c) => {
                    const sel = candidateToSelection(c);
                    const active =
                      !creating &&
                      selection !== null &&
                      ((sel.mode === 'existing-bond' &&
                        selection.mode === 'existing-bond' &&
                        selection.bondId === sel.bondId) ||
                        (sel.mode === 'create-native' &&
                          selection.mode === 'create-native' &&
                          selection.externalReference === sel.externalReference));
                    return (
                      <button
                        key={`${c.source}:${c.bondId ?? c.externalReference ?? c.label}`}
                        type="button"
                        className="flex items-center justify-between rounded border px-3 py-2 text-left"
                        style={{
                          borderColor: active ? 'var(--brand)' : 'var(--border-default)',
                          borderLeftWidth: active ? '2px' : '1px',
                          background: active ? 'var(--bg-subtle)' : 'var(--bg-surface)',
                        }}
                        onClick={() => {
                          setCreating(false);
                          setSelection(sel);
                        }}
                      >
                        <span className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium">{c.label}</span>
                          <span className="text-2xs" style={{ color: 'var(--fg-3)' }}>
                            {c.source === 'external' ? 'externo' : 'nativo'}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                  {candidates.length === 0 && (
                    <p className="text-xs" style={{ color: 'var(--fg-3)' }}>
                      Nenhum vínculo encontrado para esta pessoa.
                    </p>
                  )}
                  {!externalAvailable && (
                    <p className="text-2xs" style={{ color: 'var(--fg-3)' }}>
                      Nenhum sistema externo conectado — mostrando apenas vínculos nativos.
                    </p>
                  )}
                  <button
                    type="button"
                    className="rounded border border-dashed px-3 py-2 text-sm"
                    style={{
                      borderColor: creating ? 'var(--brand)' : 'var(--border-strong)',
                      color: 'var(--fg-2)',
                    }}
                    onClick={() => {
                      setSelection(null);
                      setCreating(true);
                    }}
                  >
                    + Criar vínculo novo
                  </button>
                  {creating && (
                    <select
                      className="rounded border px-3 py-2 text-sm"
                      style={{ borderColor: 'var(--border-default)' }}
                      value={createBondType}
                      onChange={(e) => setCreateBondType(e.target.value)}
                    >
                      {BOND_TYPES_CATALOG.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>

            <div
              className="flex flex-col gap-2 border-t pt-4"
              style={{ borderColor: 'var(--border-default)' }}
            >
              <span
                className="text-2xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--fg-3)' }}
              >
                Papel nesta relação
              </span>
              <select
                className="rounded border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-default)' }}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              {isTitular && (
                <p
                  className="rounded border p-3 text-xs"
                  style={{
                    borderColor: 'var(--border-default)',
                    background: 'var(--bg-subtle)',
                    color: 'var(--fg-2)',
                  }}
                >
                  Ao avalizar, você confirma a identidade desta pessoa como titular — verificação
                  por operador, válida pelo período definido na política de validade do tenant.
                  Papel não-titular apenas registra o vínculo, sem confirmar identidade.
                </p>
              )}
            </div>
          </div>

          <div
            className="flex items-center justify-between gap-2 border-t p-4"
            style={{ borderColor: 'var(--border-default)' }}
          >
            <button
              type="button"
              className="text-sm font-medium"
              style={{ color: 'var(--brand)' }}
              onClick={onClose}
              disabled={isPending}
            >
              Cancelar
            </button>
            <div className="flex items-center gap-2">
              {isTitular && (
                <button
                  type="button"
                  className="rounded border px-4 py-2 text-sm"
                  style={{ borderColor: 'var(--border-default)' }}
                  onClick={() => submit(false)}
                  disabled={!canSubmit}
                >
                  Registrar sem avalizar
                </button>
              )}
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => submit(isTitular)}
                disabled={!canSubmit}
              >
                {isPending ? 'Vinculando…' : isTitular ? 'Vincular e avalizar titular' : 'Vincular'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
